<template>
  <sp-text-field
    ref="textField"
    v-model="model"
    class="sp-search-box"
    :class="classModifiers"
    :density="density"
    :densitysmcompact="densitysmcompact"
    :input-test-id="inputTestId"
    :readonly="$props.inputmode === 'none'"
    :focus-on-open="focusOnOpen"
    :placeholder="placeholder"
    @input="onTextInput"
    @keydown.space.stop
  >
    <div slot="prepend" class="prepend">
      <slot name="prepend" @click="onPrependClick">
        <sp-icon name="search-filled" class="sp-search-box__icon"></sp-icon>
      </slot>
    </div>
    <div slot="append" class="append">
      <slot name="append" @click="onAppendClick">
        <sp-button
          v-if="showClearButton"
          class="sp-search-box__trailing-action"
          size="small"
          color="surface"
          icon="close-filled"
          :densitysmcompact="densitysmcompact"
        ></sp-button>
      </slot>
    </div>
  </sp-text-field>
</template>

<script setup>
import { computed, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { firstDetail, isElementReport } from "#ce/utils/events";
import { isPresent, toBoolean } from "#ce/utils/props";
import { props as searchBoxProps } from "./props.js";

// The host `value` attribute is a programmatic set channel, adopted below.
// inheritAttrs is off so the attribute never falls through onto the
// sp-text-field root, where it would override the `:value="model"` binding.
defineOptions({ inheritAttrs: false });

const emit = defineEmits(["input", "text-input", "click-prepend", "click-append"]);
const { emitBubbling } = useHostEmit();

const props = defineProps({
  ...searchBoxProps,
});

// `text-input` is emitted where the user's edit enters (typing, clear), never
// from a watcher on the model — a `value` attribute or property sync is mirrored
// silently, so hosts can treat every `text-input` as user input. Property writes
// always adopt, like a native input's `value`.
const attrs = useAttrs();
const model = ref(attrs.value);
watch(
  () => attrs.value,
  (value) => (model.value = value),
);

const textField = useTemplateRef("textField");

const { exposeProperties } = useExpose();

exposeProperties({
  inputElement: {
    configurable: true,
    get: () => textField.value?.inputElement,
  },
  value: {
    configurable: true,
    get: () => model.value,
    set: (value) => (model.value = value),
  },
});

// Called from the user paths only (the inner field's `input` fires for user
// edits alone), so every dispatched `input`/`text-input` reports an actual edit.
function setModel(value) {
  model.value = value;
  emitBubbling("input", value);
  emit("text-input", value);
}

const clearable = computed(() => toBoolean(props.clearable));
const focusOnOpen = computed(() => isPresent(props.focusOnOpen));

const showClearButton = computed(() => clearable.value && model.value?.length > 0);

function onTextInput(event) {
  if (!isElementReport(event)) {
    return;
  }

  setModel(firstDetail(event));
}

function onPrependClick() {
  emit("click-prepend");
}

function onAppendClick() {
  if (clearable.value && model.value) {
    setModel("");
  }
  emit("click-append");
}

const classModifiers = computed(() => ({
  "--clearable": toBoolean(props.clearable),
}));
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-search-box {
  // The typed value must read as body text, not the placeholder/outline grey.
  // The global `--sp-comp-text-field-color` default is aliased to the outline
  // colour, so without this the entered query renders in the same grey as the
  // placeholder. Use the on-surface text colour (the field background is a
  // surface), matching the other filter consumers.
  --sp-comp-text-field-color: var(--sp-sys-color-on-surface);

  // Mirror the inline padding onto the compact token too — otherwise compact
  // density falls back to the sys form-field padding and re-indents the input
  // past the leading glass icon, breaking the glass-flush layout.
  --sp-comp-text-field-padding-inline: 0 0.5rem;
  --sp-comp-text-field-compact-padding-inline: 0 0.5rem;
  &.--clearable {
    --sp-comp-text-field-padding-inline: 0;
    --sp-comp-text-field-compact-padding-inline: 0;
  }
}

.sp-search-box__icon {
  --fill: var(--sp-comp-search-box-glass-icon-fill, var(--sp-sys-form-field-placeholder-color, #bbb));
}

.sp-search-box__trailing-action::part(icon) {
  --icon-size: var(--sp-comp-search-box-icon-size, var(--sp-sys-icon-size-medium, 2rem));
  --text-color: var(--sp-comp-search-box-clear-icon-fill, var(--sp-sys-color-tertiary, #333));
}

.append,
.prepend {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
